/** Debug spawn scene — choose archetype counts and spawn enemies at random angles around the player. */
import Phaser from 'phaser';
import { ConfigLoader } from '../../systems/ConfigLoader';
import { GameScene } from '../GameScene';
import { randomAngle, angleToDirection, randomInRange } from '../../utils/Math';
import { crispText, crispButton, setupCrispCamera } from '../../ui/CrispUtil';
import { Font, Hex, TextStyle, ButtonColor, applyButtonHover } from '../../ui/Theme';
import { GAME_WIDTH } from '../../utils/Constants';

const MAX_COUNT = 10;
const SPAWN_MIN_DIST = 150;
const SPAWN_MAX_DIST = 300;

interface SpawnRow {
  archetypeId: string;
  count: number;
  countText: Phaser.GameObjects.Text;
}

/**
 * Renders +/- controls for every loaded enemy archetype. Clicking SPAWN places the selected
 * quantities at random positions between SPAWN_MIN_DIST and SPAWN_MAX_DIST pixels from the player.
 */
export class DebugSpawnScene extends Phaser.Scene {
  private rows: SpawnRow[] = [];

  constructor() {
    super({ key: 'DebugSpawnScene' });
  }

  create(): void {
    this.rows = [];
    setupCrispCamera(this);
    const width = GAME_WIDTH;

    this.cameras.main.setBackgroundColor(Hex.sceneBg);

    crispText(this, width / 2, 30, 'SPAWN ENEMIES', TextStyle.debugTitle).setOrigin(0.5);

    const archetypes = ConfigLoader.enemies;
    const startY = 80;
    const lineHeight = 28;
    const labelX = width / 2 - 80;
    const minusBtnX = width / 2 + 40;
    const countX = width / 2 + 70;
    const plusBtnX = width / 2 + 100;

    for (let i = 0; i < archetypes.length; i++) {
      const arch = archetypes[i];
      const y = startY + i * lineHeight;

      const name = arch.id.charAt(0).toUpperCase() + arch.id.slice(1);
      crispText(this, labelX, y, name, TextStyle.body).setOrigin(1, 0.5);

      const minusBtn = crispText(this, minusBtnX, y, '-', { ...TextStyle.body, color: Hex.debugDefault })
        .setOrigin(0.5).setInteractive({ useHandCursor: true });

      const countText = crispText(this, countX, y, '0', { ...TextStyle.body, color: '#ffffff' }).setOrigin(0.5);

      const plusBtn = crispText(this, plusBtnX, y, '+', { ...TextStyle.body, color: Hex.debugDefault })
        .setOrigin(0.5).setInteractive({ useHandCursor: true });

      const row: SpawnRow = { archetypeId: arch.id, count: 0, countText };
      this.rows.push(row);

      minusBtn.on('pointerdown', () => {
        row.count = Math.max(0, row.count - 1);
        row.countText.setText(String(row.count));
      });

      plusBtn.on('pointerdown', () => {
        row.count = Math.min(MAX_COUNT, row.count + 1);
        row.countText.setText(String(row.count));
      });

      applyButtonHover(minusBtn, ButtonColor.debug);
      applyButtonHover(plusBtn, ButtonColor.debug);
    }

    const btnY = startY + archetypes.length * lineHeight + 30;

    crispButton(this, width / 2, btnY, 'SPAWN',
      Font.sizeSmall, ButtonColor.primary, () => this.doSpawn());

    this.input.keyboard!.on('keydown-ESC', () => this.scene.start('DebugSettingsScene'));
  }

  private doSpawn(): void {
    const gs = this.scene.get('GameScene') as GameScene;
    if (!gs?.enemyManager || !gs?.player) return;

    const px = gs.player.sprite.x;
    const py = gs.player.sprite.y;

    for (const row of this.rows) {
      for (let i = 0; i < row.count; i++) {
        const angle = randomAngle();
        const dist = randomInRange(SPAWN_MIN_DIST, SPAWN_MAX_DIST);
        const dir = angleToDirection(angle);
        const x = px + dir.x * dist;
        const y = py + dir.y * dist;
        gs.enemyManager.spawnAt(x, y, row.archetypeId);
      }
    }

    this.scene.start('DebugSettingsScene');
  }
}
